import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { IdleSessionConfig } from '../models/idle-session-config';

export const DEFAULT_IDLE_TIMEOUT_MS = 40 * 60 * 1000;
export const DEFAULT_IDLE_WARNING_BEFORE_TIMEOUT_MS = 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class IdleSession {
  private readonly document = inject(DOCUMENT);

  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
  ] as const;

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private warningTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private removeListeners: Array<() => void> = [];
  private onTimeout: (() => void) | null = null;
  private onWarning: (() => void) | null = null;
  private onActivity: (() => void) | null = null;

  start({
    onTimeout,
    onWarning,
    onActivity,
    timeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
    warningBeforeTimeoutMs = DEFAULT_IDLE_WARNING_BEFORE_TIMEOUT_MS,
  }: IdleSessionConfig): void {
    this.stop();
    this.onTimeout = onTimeout;
    this.onWarning = onWarning ?? null;
    this.onActivity = onActivity ?? null;

    const eventTarget = this.document.defaultView ?? this.document;

    this.removeListeners = this.activityEvents.map((eventName) => {
      const listener = () => this.reset(timeoutMs, warningBeforeTimeoutMs);

      eventTarget.addEventListener(eventName, listener, { passive: true });

      return () => eventTarget.removeEventListener(eventName, listener);
    });

    this.reset(timeoutMs, warningBeforeTimeoutMs);
  }

  stop(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }

    this.removeListeners.forEach((removeListener) => removeListener());
    this.removeListeners = [];
    this.onTimeout = null;
    this.onWarning = null;
    this.onActivity = null;
  }

  reset(
    timeoutMs: number = DEFAULT_IDLE_TIMEOUT_MS,
    warningBeforeTimeoutMs: number = DEFAULT_IDLE_WARNING_BEFORE_TIMEOUT_MS,
  ): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
    }

    this.onActivity?.();

    const warningDelay = timeoutMs - warningBeforeTimeoutMs;

    if (warningDelay > 0 && this.onWarning) {
      this.warningTimeoutId = setTimeout(() => {
        this.onWarning?.();
      }, warningDelay);
    }

    this.timeoutId = setTimeout(() => {
      this.onTimeout?.();
    }, timeoutMs);
  }
}
